import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, Popconfirm, message, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../api'
import useStore from '../store'

export default function ProjectsPage() {
  const [data, setData] = useState([])
  const [owners, setOwners] = useState([])
  const [open, setOpen] = useState(false)
  const [ownerOpen, setOwnerOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [newOwner, setNewOwner] = useState('')
  const [form] = Form.useForm()
  const { user } = useStore()
  const canEdit = user?.role !== 'readonly'

  const load = () => api.get('/projects').then(setData)
  const loadOwners = () => api.get('/owners').then(setOwners)

  useEffect(() => { load(); loadOwners() }, [])

  const openModal = (record = null) => {
    setEditing(record)
    if (record) {
      form.setFieldsValue({
        ...record,
        start_date: dayjs(record.start_date),
        owner_ids: (record.owners || []).map(o => o.id)
      })
    } else {
      form.setFieldsValue({ status: 'active', region: 'domestic', owner_ids: [] })
    }
    setOpen(true)
  }

  const onSave = async () => {
    const values = await form.validateFields()
    const payload = { ...values, start_date: values.start_date.format('YYYY-MM-DD') }
    try {
      if (editing) { await api.put(`/projects/${editing.id}`, payload) }
      else { await api.post('/projects', payload) }
      message.success('保存成功')
      setOpen(false)
      load()
    } catch (err) { message.error(err) }
  }

  const addOwner = async () => {
    if (!newOwner.trim()) return
    try {
      await api.post('/owners', { name: newOwner.trim() })
      setNewOwner('')
      loadOwners()
    } catch (err) { message.error(err) }
  }

  const deleteOwner = async (id) => {
    await api.delete(`/owners/${id}`)
    loadOwners()
  }

  const columns = [
    { title: '项目名称', dataIndex: 'name' },
    {
      title: '负责人', dataIndex: 'owners', width: 160,
      render: owners => owners?.length
        ? owners.map(o => <Tag key={o.id}>{o.name}</Tag>)
        : '-'
    },
    { title: '区域', dataIndex: 'region', width: 80, render: v => v === 'domestic' ? <Tag color="blue">国内</Tag> : <Tag color="orange">国外</Tag> },
    { title: '开始日期', dataIndex: 'start_date', render: v => dayjs(v).format('YYYY-MM-DD'), width: 110 },
    { title: '状态', dataIndex: 'status', width: 80, render: v => v === 'active' ? <Tag color="green">进行中</Tag> : <Tag>已结束</Tag> },
    { title: '备注', dataIndex: 'note', ellipsis: true },
    canEdit && {
      title: '操作', width: 100, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)} />
          <Popconfirm title="确认删除？" onConfirm={async () => { await api.delete(`/projects/${r.id}`); load() }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ].filter(Boolean)

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>新增项目</Button>}
        {canEdit && <Button onClick={() => setOwnerOpen(true)}>管理负责人</Button>}
      </Space>
      <Table dataSource={data} columns={columns} rowKey="id" />

      <Modal title={editing ? '编辑项目' : '新增项目'} open={open} onOk={onSave} onCancel={() => setOpen(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="项目名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="owner_ids" label="负责人">
            <Select mode="multiple" allowClear placeholder="选择负责人（可多选）">
              {owners.map(o => <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="region" label="区域" rules={[{ required: true }]}>
            <Select><Select.Option value="domestic">国内</Select.Option><Select.Option value="overseas">国外</Select.Option></Select>
          </Form.Item>
          <Form.Item name="start_date" label="开始日期" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="status" label="状态">
            <Select><Select.Option value="active">进行中</Select.Option><Select.Option value="closed">已结束</Select.Option></Select>
          </Form.Item>
          <Form.Item name="note" label="备注"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="管理负责人" open={ownerOpen} onCancel={() => setOwnerOpen(false)} footer={null}>
        <Space style={{ marginBottom: 12 }}>
          <Input value={newOwner} onChange={e => setNewOwner(e.target.value)} placeholder="输入负责人姓名" onPressEnter={addOwner} />
          <Button type="primary" onClick={addOwner}>添加</Button>
        </Space>
        <Table
          dataSource={owners} rowKey="id" pagination={false} size="small"
          columns={[
            { title: '姓名', dataIndex: 'name' },
            { title: '操作', width: 80, render: (_, r) => (
              <Popconfirm title="确认删除？" onConfirm={() => deleteOwner(r.id)}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          ]}
        />
      </Modal>
    </div>
  )
}
